import { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutGrid, ShieldCheck, ShieldOff, History, Info, Users, X, ChevronDown, AlertTriangle, Check, Ban,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { colors, getNeumorphicShadow } from '@/lib/designSystem';

const STATE_STYLE = {
  ACTIVE: { bg: '#DCFCE7', fg: '#166534', label: 'Active' },
  BLOCKED: { bg: '#FEE2E2', fg: '#991B1B', label: 'Blocked' },
  NO_ROLE_ACCESS: { bg: '#F1F5F9', fg: '#475569', label: 'No role access' },
  INACTIVE_ORG_DISABLED: { bg: '#FEF3C7', fg: '#92400E', label: 'Inactive — org disabled' },
};

const Pill = ({ bg, fg, children, testId }) => (
  <span className="text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap"
        style={{ background: bg, color: fg }} data-testid={testId}>
    {children}
  </span>
);

const AppCard = ({ app, busy, onToggle, onManageUsers, c, theme }) => {
  const [open, setOpen] = useState(false);
  const governed = app.is_governed;
  const enabled = app.is_enabled === true;
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl p-5 sm:p-6"
      style={{ background: c.cardBg || c.bgSecondary, boxShadow: getNeumorphicShadow(theme) }}
      data-testid={`app-card-${app.app_code}`}
    >
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-lg font-semibold" style={{ color: c.textPrimary }}>{app.display_name}</span>
            {governed ? (
              <Pill bg={enabled ? '#DCFCE7' : '#FEE2E2'} fg={enabled ? '#166534' : '#991B1B'}
                    testId={`app-status-${app.app_code}`}>
                Organisation access: {enabled ? 'Enabled' : 'Disabled'}
              </Pill>
            ) : (
              <Pill bg="#F1F5F9" fg="#475569" testId={`app-status-${app.app_code}`}>
                Organisation access: Always available
              </Pill>
            )}
          </div>
          <p className="text-sm mt-1" style={{ color: c.textSecondary }}>
            {app.description || 'Access is governed by each user’s role and outlet.'}
          </p>
          <p className="text-xs mt-2" style={{ color: c.textSecondary }} data-testid={`app-users-count-${app.app_code}`}>
            {app.users_with_access} of {app.org_member_count} users can open this app
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 lg:justify-end">
          <button
            onClick={() => onManageUsers(app)}
            data-testid={`app-manage-users-${app.app_code}`}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium w-full sm:w-auto justify-center"
            style={{ background: c.bgSecondary, color: c.textPrimary, transition: 'opacity 150ms' }}
          >
            <Users size={16} /> Manage users
          </button>
          {governed && app.can_manage_entitlement && (
            <button
              onClick={() => onToggle(app)}
              disabled={busy}
              data-testid={`app-toggle-${app.app_code}`}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-white disabled:opacity-60 w-full sm:w-auto justify-center"
              style={{ background: enabled ? '#DC2626' : '#16A34A', transition: 'opacity 150ms' }}
            >
              {enabled ? <ShieldOff size={16} /> : <ShieldCheck size={16} />}
              {busy ? 'Saving…' : enabled ? 'Disable for organisation' : 'Enable for organisation'}
            </button>
          )}
          {governed && !app.can_manage_entitlement && (
            <Pill bg={c.bgSecondary} fg={c.textSecondary} testId={`app-readonly-${app.app_code}`}>
              View only — platform administrators manage this
            </Pill>
          )}
        </div>
      </div>

      <button onClick={() => setOpen((v) => !v)} data-testid={`app-details-toggle-${app.app_code}`}
              className="mt-4 flex items-center gap-1 text-xs" style={{ color: c.textSecondary }}>
        <ChevronDown size={14} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />
        Details
      </button>
      {open && (
        <div className="mt-2 text-xs space-y-1" style={{ color: c.textSecondary }} data-testid={`app-details-${app.app_code}`}>
          <div>Internal app key: <code>{app.app_code}</code></div>
          {app.legacy_app_codes?.length > 0 && <div>Legacy key still accepted: <code>{app.legacy_app_codes.join(', ')}</code></div>}
          <div>Module key: <code>{app.module_code}</code></div>
          {governed && (
            <div data-testid={`app-record-${app.app_code}`}>
              {app.has_entitlement_record
                ? `Last change: ${app.last_change_source || 'ADMIN'} · ${app.updated_by || ''} · ${app.updated_at || ''}`
                : 'No entitlement record yet — organisation access is off by default'}
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

const ConfirmDialog = ({ app, onCancel, onConfirm, busy, c, theme }) => {
  const disabling = app.is_enabled === true;
  const [typed, setTyped] = useState('');
  const ready = !disabling || typed.trim().toUpperCase() === 'DISABLE';
  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4"
         style={{ background: 'rgba(0,0,0,0.5)' }} data-testid="app-confirm-dialog">
      <motion.div
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
        style={{ background: c.cardBg || c.bgSecondary, boxShadow: getNeumorphicShadow(theme) }}
      >
        <div className="flex items-start justify-between gap-4">
          <h2 className="text-lg font-semibold" style={{ color: c.textPrimary }}>
            {disabling ? `Disable ${app.display_name} for this organisation?` : `Enable ${app.display_name} for this organisation?`}
          </h2>
          <button onClick={onCancel} data-testid="app-confirm-cancel-x" style={{ color: c.textSecondary }}><X size={18} /></button>
        </div>

        {disabling ? (
          <div className="mt-4 text-sm space-y-3" style={{ color: c.textSecondary }}>
            <div className="flex items-start gap-2 p-3 rounded-xl" style={{ background: '#FEF3C7', color: '#92400E' }}>
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <span>Everyone in this organisation loses access to {app.display_name} immediately, including users who have it allowed individually.</span>
            </div>
            <ul className="list-disc pl-5 space-y-1">
              <li>Existing business data is <strong>not</strong> deleted.</li>
              <li>User access settings are kept — they simply become inactive.</li>
              <li>Integrations and background workers are not deleted automatically.</li>
              <li>Re-enabling restores access according to the existing data and configuration.</li>
            </ul>
            <label className="block text-xs mt-2" style={{ color: c.textSecondary }}>
              Type <strong>DISABLE</strong> to confirm
              <input
                value={typed} onChange={(e) => setTyped(e.target.value)} autoFocus
                data-testid="app-confirm-disable-input"
                className="mt-1 w-full px-3 py-2 rounded-xl border outline-none"
                style={{ background: c.bgSecondary, color: c.textPrimary, borderColor: c.border || '#CBD5E1' }}
              />
            </label>
          </div>
        ) : (
          <div className="mt-4 text-sm space-y-2" style={{ color: c.textSecondary }}>
            <p>This lets the organisation use {app.display_name}. It does not grant the app to anyone yet.</p>
            <p>After enabling, choose who can open it under <strong>Manage users</strong>. Roles and outlet scope still apply.</p>
          </div>
        )}

        <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <button onClick={onCancel} data-testid="app-confirm-cancel"
                  className="px-4 py-2.5 rounded-xl text-sm font-medium"
                  style={{ background: c.bgSecondary, color: c.textPrimary }}>Cancel</button>
          <button
            onClick={onConfirm} disabled={!ready || busy} data-testid="app-confirm-submit"
            className="px-4 py-2.5 rounded-xl text-sm font-medium text-white disabled:opacity-50"
            style={{ background: disabling ? '#DC2626' : '#16A34A' }}
          >
            {busy ? 'Saving…' : disabling ? 'Disable for organisation' : 'Enable for organisation'}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

const UsersPanel = ({ data, busyUser, onClose, onSetAccess, currentUserId, c, theme }) => {
  const orgDisabled = data.org_access === 'DISABLED';
  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4"
         style={{ background: 'rgba(0,0,0,0.5)' }} data-testid="app-users-panel">
      <motion.div
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl p-5 sm:p-6 max-h-[90vh] overflow-y-auto"
        style={{ background: c.cardBg || c.bgSecondary, boxShadow: getNeumorphicShadow(theme) }}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold" style={{ color: c.textPrimary }}>{data.display_name} — user access</h2>
            <p className="text-sm mt-1" style={{ color: c.textSecondary }}>
              Who inside this organisation can open the app. Roles and outlet scope still apply.
            </p>
          </div>
          <button onClick={onClose} data-testid="app-users-close" style={{ color: c.textSecondary }}><X size={18} /></button>
        </div>

        {orgDisabled && (
          <div className="mt-4 p-3 rounded-xl text-sm flex items-start gap-2"
               style={{ background: '#FEF3C7', color: '#92400E' }} data-testid="org-access-inactive-banner">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <span>Organisation access disabled — user access settings are currently inactive. Nothing is deleted; enable the organisation to restore them.</span>
          </div>
        )}

        <div className="mt-4 space-y-3">
          {data.users.length === 0 && (
            <p className="text-sm" style={{ color: c.textSecondary }} data-testid="app-users-empty">No users in this organisation.</p>
          )}
          {data.users.map((u) => {
            const s = STATE_STYLE[u.effective_access] || STATE_STYLE.NO_ROLE_ACCESS;
            const blocked = u.explicit_override === false;
            const isSelf = u.user_id === currentUserId;
            return (
              <div key={u.user_id} className="rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                   style={{ background: c.bgSecondary }} data-testid={`app-user-row-${u.user_id}`}>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium" style={{ color: c.textPrimary }}>{u.user_name}</span>
                    <Pill bg={s.bg} fg={s.fg} testId={`app-user-state-${u.user_id}`}>{s.label}</Pill>
                  </div>
                  <p className="text-xs mt-1 break-all" style={{ color: c.textSecondary }}>{u.user_email}</p>
                  <p className="text-xs mt-1" style={{ color: c.textSecondary }}>
                    {(u.roles || []).join(', ') || 'No role'}
                    {u.outlets?.length ? ` · ${u.outlets.join(', ')}` : ''}
                  </p>
                  <p className="text-xs mt-1" style={{ color: c.textSecondary }} data-testid={`app-user-reason-${u.user_id}`}>
                    {isSelf ? 'This is you — another administrator must change your access. ' : ''}{u.reason}
                  </p>
                </div>
                <button
                  onClick={() => onSetAccess(u, blocked)}
                  disabled={busyUser === u.user_id || isSelf || (!blocked && !u.role_grants_app)}
                  title={isSelf ? 'This is you — ask another administrator to change your access' : undefined}
                  data-testid={`app-user-access-toggle-${u.user_id}`}
                  className="flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-medium disabled:opacity-40 w-full sm:w-auto"
                  style={{ background: blocked ? '#16A34A' : '#FEE2E2', color: blocked ? '#FFFFFF' : '#991B1B' }}
                >
                  {blocked ? <><Check size={15} /> Allow</> : <><Ban size={15} /> Block</>}
                </button>
              </div>
            );
          })}
        </div>
      </motion.div>
    </div>
  );
};

const AuditList = ({ entries, c }) => (
  <div className="mt-8" data-testid="org-module-audit">
    <div className="flex items-center gap-2 mb-3" style={{ color: c.textPrimary }}>
      <History size={16} /> <span className="font-semibold">Entitlement audit — 1OS Bills</span>
    </div>
    {entries.length === 0 ? (
      <p className="text-sm" style={{ color: c.textSecondary }} data-testid="org-module-audit-empty">No changes recorded.</p>
    ) : (
      <ul className="space-y-2">
        {entries.map((e) => (
          <li key={e.audit_id} className="text-sm break-words" style={{ color: c.textSecondary }} data-testid="org-module-audit-entry">
            {String(e.timestamp)} · {e.source} · {e.actor_user_id} · {String(e.old_value?.is_enabled)} → {String(e.new_value?.is_enabled)}
          </li>
        ))}
      </ul>
    )}
  </div>
);

export default function OrgApplications() {
  const { user } = useAuth();
  const { theme } = useTheme();
  const c = colors[theme];
  const isSuper = user?.is_super_admin === true;
  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState(isSuper ? '' : user?.org_id || '');
  const [data, setData] = useState(null);
  const [audit, setAudit] = useState([]);
  const [confirming, setConfirming] = useState(null);
  const [usersFor, setUsersFor] = useState(null);
  const [busy, setBusy] = useState(null);
  const [busyUser, setBusyUser] = useState(null);
  const [error, setError] = useState('');

  const msg = (e, fallback) => e?.response?.data?.detail?.detail || e?.response?.data?.detail || fallback;

  useEffect(() => {
    if (!isSuper) return;
    api.getOrganizations().then((r) => {
      const list = r.data || [];
      setOrgs(list);
      setOrgId((prev) => prev || list[0]?.org_id || '');
    }).catch(() => setError('Failed to load organizations'));
  }, [isSuper]);

  const load = useCallback(async () => {
    if (!orgId) return;
    setError('');
    try {
      const r = await api.getOrgApplications(orgId);
      setData(r.data);
      const a = await api.getOrgModuleAudit(orgId, 'bills');
      setAudit(a.data.entries || []);
    } catch (e) {
      setError(msg(e, 'Failed to load applications'));
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const applyToggle = async () => {
    const app = confirming;
    setBusy(app.app_code);
    try {
      await api.setOrgModule(orgId, app.module_code, !app.is_enabled);
      setConfirming(null);
      await load();
      if (usersFor) await openUsers(app, true);
    } catch (e) {
      setError(msg(e, 'Failed to update organisation access'));
      setConfirming(null);
    } finally {
      setBusy(null);
    }
  };

  const openUsers = async (app, silent = false) => {
    if (!silent) setError('');
    try {
      const r = await api.getOrgApplicationUsers(orgId, app.app_code);
      setUsersFor(r.data);
    } catch (e) {
      setError(msg(e, 'Failed to load user access'));
    }
  };

  const setUserAccess = async (u, allow) => {
    setBusyUser(u.user_id);
    try {
      await api.setOrgApplicationUserAccess(orgId, usersFor.app_code, u.user_id, allow);
      await openUsers({ app_code: usersFor.app_code }, true);
      await load();
    } catch (e) {
      setError(msg(e, 'Failed to update user access'));
    } finally {
      setBusyUser(null);
    }
  };

  const apps = useMemo(() => data?.applications || [], [data]);

  return (
    <div className="min-h-screen p-4 sm:p-6 lg:p-8" data-testid="org-applications-page">
      <div className="mb-6">
        <h1 className="text-3xl sm:text-4xl font-bold mb-2 flex items-center gap-3" style={{ color: c.textPrimary }}>
          <LayoutGrid /> Applications
        </h1>
        <p className="text-sm md:text-base" style={{ color: c.textSecondary }}>
          {data?.org_name ? `${data.org_name} — ` : ''}which 1OS applications this organisation may use.
        </p>
      </div>

      <div className="mb-6 p-4 rounded-2xl text-sm flex items-start gap-2"
           style={{ background: c.bgSecondary, color: c.textSecondary }} data-testid="two-level-explainer">
        <Info size={16} className="mt-0.5 shrink-0" />
        <span>
          <strong style={{ color: c.textPrimary }}>Organisation access</strong> decides whether this organisation may use an app at all.{' '}
          <strong style={{ color: c.textPrimary }}>User access</strong> decides who inside it can open that app.
          A user can never get in while organisation access is disabled.
        </span>
      </div>

      {isSuper && (
        <select
          value={orgId} onChange={(e) => setOrgId(e.target.value)}
          data-testid="org-applications-org-select"
          className="mb-6 w-full sm:w-auto px-4 py-2.5 rounded-xl border"
          style={{ background: c.bgSecondary, color: c.textPrimary }}
        >
          {orgs.map((o) => <option key={o.org_id} value={o.org_id}>{o.org_name}</option>)}
        </select>
      )}

      {error && <p className="mb-4 text-sm text-red-600" data-testid="org-applications-error">{String(error)}</p>}

      <div className="space-y-4">
        {apps.map((app) => (
          <AppCard
            key={app.app_code} app={app} busy={busy === app.app_code}
            onToggle={setConfirming} onManageUsers={openUsers} c={c} theme={theme}
          />
        ))}
      </div>

      <p className="mt-4 text-xs flex items-start gap-1" style={{ color: c.textSecondary }} data-testid="org-applications-propagation-note">
        <Info size={12} className="mt-0.5 shrink-0" />
        Changes take effect immediately on Control Room Bills routes, access context and app launch; already-open app sessions see it on their next request (at most 60 minutes for claim-only consumers).
      </p>

      <AuditList entries={audit} c={c} />

      <AnimatePresence>
        {confirming && (
          <ConfirmDialog
            app={confirming} busy={busy === confirming.app_code}
            onCancel={() => setConfirming(null)} onConfirm={applyToggle} c={c} theme={theme}
          />
        )}
        {usersFor && (
          <UsersPanel
            data={usersFor} busyUser={busyUser} onClose={() => setUsersFor(null)}
            onSetAccess={setUserAccess} currentUserId={user?.user_id} c={c} theme={theme}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
